import React, { useContext, useRef, useState } from "react";
import Cropper from "react-easy-crop";
import getCroppedImg from "./cropImage";
import LanguageContext from "../../helper/LanguageContext";
import "./ImageCropper.css";

const MAX_EDGE = 1024;
const JPEG_QUALITY = 0.82;

function createImage(url) {
  return new Promise((resolve, reject) => {
    const image = new Image();
    image.addEventListener("load", () => resolve(image));
    image.addEventListener("error", reject);
    image.src = url;
  });
}

async function compressSquareJpeg(blob) {
  const url = URL.createObjectURL(blob);
  try {
    const image = await createImage(url);
    const edge = Math.min(MAX_EDGE, image.width, image.height);
    const canvas = document.createElement("canvas");
    canvas.width = edge;
    canvas.height = edge;
    const ctx = canvas.getContext("2d");
    ctx.fillStyle = "#ffffff";
    ctx.fillRect(0, 0, edge, edge);
    ctx.drawImage(image, 0, 0, edge, edge);
    const compressed = await new Promise((resolve) => {
      canvas.toBlob(resolve, "image/jpeg", JPEG_QUALITY);
    });
    return compressed || blob;
  } finally {
    URL.revokeObjectURL(url);
  }
}

function wrapDegrees(value) {
  const rounded = Math.round(value);
  return ((rounded + 180) % 360 + 360) % 360 - 180;
}

function readImageFile(file) {
  return new Promise((resolve, reject) => {
    if (!file || !String(file.type || "").startsWith("image/")) {
      reject(new Error("not-image"));
      return;
    }
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = () => reject(reader.error);
    reader.readAsDataURL(file);
  });
}

export default function ImageCropper({ changeImage, onClose, mode = "profile" }) {
  const { isGerm } = useContext(LanguageContext);
  const isLogo = mode === "logo";
  const inputRef = useRef(null);
  const [image, setImage] = useState("");
  const [fileName, setFileName] = useState("avatar.jpg");
  const [crop, setCrop] = useState({ x: 0, y: 0 });
  const [zoom, setZoom] = useState(1);
  const [rotation, setRotation] = useState(0);
  const [croppedPixels, setCroppedPixels] = useState(null);
  const [dragOver, setDragOver] = useState(false);
  const [busy, setBusy] = useState(false);

  const loadFile = async (file) => {
    try {
      const src = await readImageFile(file);
      setImage(src);
      setFileName((file.name || "avatar").replace(/\.\w+$/, "") + ".jpg");
      setCrop({ x: 0, y: 0 });
      setZoom(1);
      setRotation(0);
      setCroppedPixels(null);
    } catch (_) {
      setImage("");
    }
  };

  const onDrop = (event) => {
    event.preventDefault();
    setDragOver(false);
    const file = event.dataTransfer?.files?.[0];
    if (file) loadFile(file);
  };

  const save = async () => {
    if (!image || !croppedPixels || busy) return;
    setBusy(true);
    try {
      const croppedUrl = await getCroppedImg(image, croppedPixels, rotation, "image/jpeg");
      const croppedBlob = await fetch(croppedUrl).then((response) => response.blob());
      URL.revokeObjectURL(croppedUrl);
      const compressed = await compressSquareJpeg(croppedBlob);
      const file = new File([compressed], fileName, { type: "image/jpeg" });
      await changeImage(file);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="avatar-picker">
      <input
        ref={inputRef}
        type="file"
        accept="image/*"
        hidden
        onChange={(event) => {
          const file = event.target.files?.[0];
          if (file) loadFile(file);
          event.target.value = "";
        }}
      />
      {!image ? (
        <div
          role="button"
          tabIndex={0}
          className={`avatar-picker-drop${dragOver ? " is-over" : ""}`}
          onClick={() => inputRef.current?.click()}
          onKeyDown={(event) => {
            if (event.key === "Enter" || event.key === " ") inputRef.current?.click();
          }}
          onDragOver={(event) => {
            event.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={onDrop}
        >
          <span className="material-symbols-outlined image-cropper__drop-icon">
            add_photo_alternate
          </span>
          <strong>{isGerm ? "Bild hierher ziehen" : "Drop an image here"}</strong>
          <span>{isGerm ? "oder klicken, um eine Datei zu wählen" : "or click to choose a file"}</span>
        </div>
      ) : (
        <>
          <p className="avatar-picker-lead">
            {isLogo
              ? (isGerm
                ? "Wähle den quadratischen Ausschnitt für das Logo. Das Bild wird in der bestmöglichen Auflösung gespeichert."
                : "Choose the square area for the logo. The image is saved at the best available resolution.")
              : (isGerm
                ? "Wähle den quadratischen Ausschnitt für das Profilbild. Das Bild wird in der bestmöglichen Auflösung gespeichert."
                : "Choose the square area for the profile photo. The image is saved at the best available resolution.")}
          </p>
          <div
            className="avatar-picker-stage"
            onDragOver={(event) => event.preventDefault()}
            onDrop={onDrop}
          >
            <Cropper
              image={image}
              crop={crop}
              zoom={zoom}
              rotation={rotation}
              aspect={1}
              cropShape="rect"
              showGrid={false}
              onCropChange={setCrop}
              onZoomChange={setZoom}
              onCropComplete={(_, pixels) => setCroppedPixels(pixels)}
            />
          </div>
          <div className="avatar-picker-rotate">
            <button
              type="button"
              className="kj-btn kj-btn--secondary kj-btn--sm"
              onClick={() => setRotation((value) => wrapDegrees(value - 90))}
            >
              <span className="material-symbols-outlined image-cropper__btn-icon">rotate_left</span>
              {isGerm ? "Links drehen" : "Rotate left"}
            </button>
            <span className="avatar-picker-degree">{wrapDegrees(rotation)}°</span>
            <button
              type="button"
              className="kj-btn kj-btn--secondary kj-btn--sm"
              onClick={() => setRotation((value) => wrapDegrees(value + 90))}
            >
              <span className="material-symbols-outlined image-cropper__btn-icon">rotate_right</span>
              {isGerm ? "Rechts drehen" : "Rotate right"}
            </button>
          </div>
          <label className="avatar-picker-tilt">
            <span>{isGerm ? "Fein" : "Fine"}</span>
            <input
              type="range"
              min={-180}
              max={180}
              step={1}
              value={wrapDegrees(rotation)}
              onChange={(event) => setRotation(Number(event.target.value))}
              aria-label={isGerm ? "Bild leicht drehen" : "Rotate image slightly"}
            />
          </label>
          <p className="avatar-picker-size">
            {isGerm ? "Ausgabegröße" : "Output size"}:{" "}
            <strong>
              {croppedPixels
                ? `${Math.min(MAX_EDGE, Math.round(croppedPixels.width))} × ${Math.min(MAX_EDGE, Math.round(croppedPixels.height))} px`
                : "—"}
            </strong>
          </p>
          <div className="avatar-picker-actions">
            <button type="button" className="kj-btn kj-btn--secondary" onClick={onClose}>
              <span className="material-symbols-outlined image-cropper__btn-icon image-cropper__btn-icon--muted">close</span>
              {isGerm ? "Abbrechen" : "Cancel"}
            </button>
            <button type="button" className="kj-btn kj-btn--primary kj-btn--internal" disabled={busy || !croppedPixels} onClick={save}>
              <span className="material-symbols-outlined image-cropper__btn-icon">check</span>
              {busy
                ? (isGerm ? "Wird gespeichert…" : "Saving…")
                : (isLogo
                  ? (isGerm ? "Logo übernehmen" : "Use logo")
                  : (isGerm ? "Profilbild übernehmen" : "Use photo"))}
            </button>
          </div>
        </>
      )}
    </div>
  );
}
